import React, { useEffect, useState } from 'react'
import { LicenseCard } from '../../components/LicenseCard'
import Button from '../../components/Button'
import PurchaseDetailsModal from '../../components/PurchaseDetailsModal'
import { useGetEvaluationCoursesListApiMutation } from '../../redux/api/getLicenseProductsApi'
import Skeleton from 'react-loading-skeleton'
import 'react-loading-skeleton/dist/skeleton.css'
import { formatAmount } from '../../utilities/helpers'
import { commercialLicensesLabel } from '../../utilities/pageConstants'
import { commonLabelName } from '../../utilities/commonConstants'

/**
 * Represents a license available for purchase.
 */
interface License {
    ProductCode: string
    id: string
    title: string
    term: string
    imageSrc: string
    unitPricewithoutMaintenance: string | 'Free'
    price: string | 'Free'
    type: 'priced' | 'free'
}

/**
 * EvaluationLicenses Component - Displays and manages evaluation licenses.
 *
 * @component
 */
const EvaluationLicenses = ({ onLoadingChange }: { onLoadingChange?: (isLoading: boolean) => void }) => {
    /** Stores selected license IDs */
    const [selectedLicenses, setSelectedLicenses] = useState<Set<string>>(new Set())

    /** API mutation to fetch license products list */
    const [GetEvaluationProductsList, { isLoading }] = useGetEvaluationCoursesListApiMutation()

    /** Stores the quantity of each selected license */
    const [quantities, setQuantities] = useState<Record<string, number>>({})

    /** Controls the modal visibility */
    const [isModalOpen, setModalOpen] = useState(false)

    /** Stores the list of available licenses */
    const [licenses, setLicenses] = useState<License[]>([])

    /** Stores details of selected licenses */
    const [selectedLicensesDetails, setSelectedLicensesDetails] = useState<
        { ProductCode: string; ProductName: string; Quantity: number; Price: string; Term: string }[]
    >([])

    /** Fetches the list of available evaluation licenses. */
    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await GetEvaluationProductsList({
                    flow: 'Commercial',
                    licenseType: 'evaluation'
                }).unwrap()

                const perpetualProductCodes = ['ENVIDLLIC', 'ENVLIC', 'IDLLIC']

                const transformedLicenses: License[] = response?.data?.map((item: any) => ({
                    ProductCode: item.ProductCode,
                    title: item.Name,
                    term: perpetualProductCodes.includes(item.ProductCode) ? 'Perpetual License' : '14 Day Trial License',
                    unitPricewithoutMaintenance: item?.UnitPrice,
                    price: item.UnitPricewithMaintenance || '',
                    imageSrc: item.imageSrc,
                    type: 'priced'
                }))

                // Sort licenses in the specified order
                const sortOrder = ['ENVEVLTLA', 'IDLEVLTLA', 'SARESSEVLTLA', 'SAREVLTLA', 'IIQEVLTLA']
                const sortedLicenses = transformedLicenses.sort((a, b) => {
                    const indexA = sortOrder.indexOf(a.ProductCode)
                    const indexB = sortOrder.indexOf(b.ProductCode)
                    return indexA - indexB
                })

                setLicenses(sortedLicenses)
            } catch (error) {
                console.error('Error fetching data:', error)
            }
        }

        fetchData()
    }, [GetEvaluationProductsList])

    // Notify parent component about loading state changes
    useEffect(() => {
        onLoadingChange?.(isLoading)
    }, [isLoading, onLoadingChange])

    /**
     * Handles the selection and deselection of a license.
     *
     * @param {string} id - The ID of the license being toggled.
     */
    const toggleLicense = (id: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
        const newSelected = new Set(selectedLicenses)
        const selectedLicense = licenses?.find((license: License) => license?.ProductCode === id)

        if (!selectedLicense) return
        const licenseDetails = {
            ProductCode: selectedLicense?.ProductCode || '',
            ProductName: selectedLicense?.title || '',
            Quantity: quantities[id] || 0,
            Price: String(selectedLicense?.unitPricewithoutMaintenance),
            Term: selectedLicense?.term
        }

        if (event.target.checked) {
            newSelected.add(id)
            setSelectedLicensesDetails((prev) => [...prev, licenseDetails])
        } else {
            newSelected.delete(id)
            setSelectedLicensesDetails((prev) => prev.filter((item) => item?.ProductCode !== id))
        }
        setSelectedLicenses(newSelected)
    }

    /**
     * Updates the quantity of a selected license.
     *
     * @param {string} id - The ID of the license.
     * @param {number} newQuantity - The updated quantity.
     */
    const handleQuantityChange = (id: string, newQuantity: number) => {
        setQuantities((prev) => {
            const updatedQuantities = {
                ...prev,
                [id]: newQuantity > 0 ? newQuantity : 0
            }

            if (newQuantity === 0) {
                const newSelected = new Set(selectedLicenses)
                newSelected.delete(id)
                setSelectedLicenses(newSelected)
                setSelectedLicensesDetails((prevDetails) => prevDetails.filter((item) => item.ProductCode !== id))
            }

            return updatedQuantities
        })
    }

    /**
     * Renders the list of available licenses as `LicenseCard` components.
     *
     * @returns {JSX.Element | null} A list of `LicenseCard` components or null if no licenses exist.
     */
    const renderLicenses = () => {
        return licenses?.map((license) => (
            <LicenseCard
                key={license.ProductCode}
                productCode={license?.ProductCode}
                title={license.title}
                term={license.term}
                imageSrc={license.imageSrc}
                price={formatAmount(Number(license.price))}
                isSelected={selectedLicenses.has(license.ProductCode)}
                onSelect={toggleLicense(license.ProductCode)}
                setQuantity={(newQuantity: number) => handleQuantityChange(license.ProductCode, newQuantity)}
                quantity={quantities[license.ProductCode]}
                isEvaluation={true}
            />
        ))
    }

    return (
        <div className='p-6 max-w-7xl mx-auto border border-gray bg-[#f9f9f9] rounded-lg'>
            <div className='mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6'>
                {isLoading ? (
                    Array.from({ length: 4 }).map((_, index) => (
                        <div key={index} className='bg-gray-200 rounded-lg h-24 p-2 mb-4'>
                            <Skeleton height={100} />
                        </div>
                    ))
                ) : licenses.length > 0 ? (
                    renderLicenses()
                ) : (
                    <div className='text-center col-span-2 py-10 text-gray-500'>{commercialLicensesLabel?.NoLicensesAvailable}</div>
                )}
            </div>

            {/* Proceed Button - Bottom Right */}
            <div className='flex justify-end mt-6'>
                <Button
                    variant='primary'
                    className={`px-6 py-2 bg-primary text-white rounded-md ${
                        selectedLicenses.size === 0 ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                    disabled={selectedLicenses.size === 0}
                    onClick={() => {
                        setModalOpen(true)
                    }}
                >
                    {commonLabelName?.proceed}
                </Button>
            </div>

            {isModalOpen && (
                <PurchaseDetailsModal
                    licenses={selectedLicensesDetails}
                    quantities={quantities}
                    onClose={() => {
                        setModalOpen(false)
                        setQuantities({})
                        setSelectedLicenses(new Set())
                        setSelectedLicensesDetails([])
                    }}
                    type='EVALUATION'
                    setSelectedLicenses={setSelectedLicenses}
                    setQuantities={setQuantities}
                    setSelectedLicensesDetails={setSelectedLicensesDetails}
                />
            )}
        </div>
    )
}

export default EvaluationLicenses
